// Dependency imports
import { Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useSelector } from 'react-redux';
import FilterListIcon from '@mui/icons-material/FilterList';
import { SearchOutlined } from '@mui/icons-material';
import Chip from '@mui/material/Chip';

// Local imports
import MultiReport from './multiReports';
import { Card, Typography } from 'components';
import SingleReport from './singleReport';
import GanttReport from './ganttReports';
import SelectConfig from '../../assets/images/selectConfig.svg';
import {
  useInferedVideosGraphData,
  useListConfigurations,
  useListInferenceModels,
  useListInferenceVideos
} from 'api/sdk';
import { useAppSelector, useAppDispatch } from 'hooks';
import { RootState } from 'store';
import { selectInferenceState, updateConfigId } from 'store/inference';
import SearchSelect from 'components/SearchSelect';

// Style imports
import Styles from './styles.module.scss';
import clsx from 'clsx';
import { setConfiguration } from 'store/configuration/configurationSlice';

const InferenceReports = () => {
  const [modelSelected, setModelSelected] = useState<string | null>(null);
  const [openState, setOpenState] = useState<number>(0);
  const [searchedVal, setSearchedVal] = useState<string>('');
  const [sortModel, setSortModel] = useState<string>('');
  const [getVideo, setVideo] = useState<any>('');
  const { data: configData } = useListConfigurations();
  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const [getConfigId, setConfigId] = useState<any>(currentConfigId);
  const siteId = useSelector(
    (data: any) => data?.auth?.profile?.session_site?.id
  );
  const { data: models } = useListInferenceModels(
    siteId,
    {
      config_id: currentConfigId
    },
    {
      query: {
        enabled: !!currentConfigId
      }
    }
  );
  const dispatch = useAppDispatch();
  const { data: indefenceVideoData, refetch: refetchInferenceVideos } =
    useListInferenceVideos(
      getConfigId,
      {
        model_id: null,
        with_graph: true
      },
      {
        query: {
          enabled: !!getConfigId
        }
      }
    );

  const [inferenceMockData, setInferenceData] = useState<any>([]);

  useEffect(() => {
    const filterData = indefenceVideoData?.data.filter((item: any) => {
      if (item.status === 'COMPLETED' && item.graph_data !== null) {
        return item;
      }
    });
    setInferenceData(filterData);
  }, [indefenceVideoData]);

  useEffect(() => {
    videoInferenceRefetch();
    refetchInferenceVideos();
  }, [currentConfigId]);

  const { videos: reportMetaData } = useSelector(selectInferenceState);

  const handleSearch = (
    searchVal: string,
    searchBy: string,
    modelId: string
  ) => {
    if (searchVal === '' && modelId === '') {
      if (indefenceVideoData) {
        const filterData = indefenceVideoData?.data.filter((item: any) => {
          if (item.status === 'COMPLETED' && item.graph_data !== null) {
            return item;
          }
        });
        setInferenceData(filterData);
        return;
      }
    }
    if (searchBy === 'filename') {
      const filterBySearch = indefenceVideoData?.data?.filter((item: any) => {
        if (
          item.name.toLowerCase().includes(searchVal.toLowerCase()) &&
          item?.model?.id.includes(modelId.toLowerCase())
        ) {
          return item;
        }
      });
      filterBySearch &&
        setInferenceData(
          filterBySearch.filter((data: any) => data?.status !== 'FAILED')
        );
    } else if (searchBy === 'model') {
      const filterBySearch = indefenceVideoData?.data?.filter((item: any) => {
        if (
          item?.model?.id
            .toString()
            .toLowerCase()
            .includes(searchVal.toLowerCase()) &&
          item.name.toLowerCase().includes(searchedVal.toLowerCase())
        ) {
          return item;
        }
      });
      setInferenceData(filterBySearch);
    }
  };

  // to get inference video
  const { refetch: videoInferenceRefetch } =
    useInferedVideosGraphData(getConfigId);

  return (
    <div className="w-full p-4 flex flex-col gap-4">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">Reports</h2>
        <div className="flex items-center justify-end gap-3">
          <SearchSelect
            label="Select Configuration"
            placeholder="Select Configuration"
            width="14vw"
            data={
              configData
                ? configData?.data.map((data: any) => {
                    return { label: data?.name, value: data?.id };
                  })
                : []
            }
            selectValue={getConfigId}
            onChange={(value: any) => {
              dispatch(setConfiguration(value));
              setConfigId(value);
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifySelf: 'flex-start',
              gap: 4,
              padding: '6px 4px',
              background: 'white',
              border: 'none',
              boxShadow:
                '0 1px 3px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.2)',
              borderRadius: '0.25rem'
            }}
          >
            <SearchOutlined />
            <input
              type="text"
              onChange={e => {
                setSearchedVal(e.target.value);
                handleSearch(e.target.value, 'filename', sortModel);
              }}
              style={{
                flex: 1,
                border: 'none',
                fontSize: '14px',
                padding: '4px',
                outline: 'none',
                background: 'transparent'
              }}
              placeholder="Search by Video Name"
            />
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            {modelSelected && (
              <Chip
                label={modelSelected}
                variant="outlined"
                onDelete={() => {
                  setSortModel('');
                  setModelSelected(null);
                  handleSearch(searchedVal, 'filename', '');
                }}
              />
            )}
            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  boxShadow: '2px 1px 5px 1px grey',
                  outline: 'none',
                  textDecoration: 'none',
                  borderRadius: '6px'
                }}
              >
                <FilterListIcon className={Styles.filtericon} />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  alignOffset={0}
                  side="bottom"
                  sideOffset={0}
                  style={{
                    background: 'white',
                    minWidth: '160px',
                    alignItems: 'center',
                    zIndex: 100,
                    border: 'none',
                    padding: '4px 0px',
                    borderRadius: '4px',
                    boxShadow: '2px 1px 5px 1px grey'
                  }}
                >
                  <DropdownMenu.Label>
                    <p className={Styles.dropdownLabel}>Filter by Model</p>
                  </DropdownMenu.Label>
                  {models?.data
                    ?.filter((item: any) => {
                      if (item.status === 'COMPLETED') {
                        return item;
                      }
                    })
                    .map((model: any, index: number) => {
                      return (
                        <DropdownMenu.Item
                          key={model.id}
                          onSelect={() => {
                            setSortModel(model.id);
                            setModelSelected(model.model_name);
                            handleSearch(model.id, 'model', sortModel);
                          }}
                          textValue={model.id}
                        >
                          <p className={Styles.dropdownItem}>
                            {model.model_name}
                          </p>
                        </DropdownMenu.Item>
                      );
                    })}
                  {models?.data?.length === 0 && (
                    <DropdownMenu.Item>
                      <p className={Styles.noModelsText}>No Models available</p>
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
      </div>
      {openState === 0 || openState === 1 ? (
        // Multi Report section (top row when needed)
        <div className="w-full h-[35vh]">
          {getConfigId !== 'Select Configuration' ? (
            inferenceMockData?.length ? (
              <MultiReport
                setVideo={setVideo}
                getVideo={getVideo}
                reportMetaData={reportMetaData}
                indefenceVideoData={inferenceMockData}
                openState={openState}
                getConfigId={getConfigId}
                setOpenState={setOpenState}
              />
            ) : (
              <div className="text-gray-500 text-sm text-center py-2 mt-[50px]">
                No data found
              </div>
            )
          ) : (
            <div className="w-full h-[70vh] flex flex-col gap-4 items-center justify-center">
              <img
                src={SelectConfig}
                alt="select_config"
                className="w-[20vw]"
              />
              <Typography sx={{ fontSize: '24px', color: '#707070' }}>
                Select configuration type to view Reports
              </Typography>
            </div>
          )}
        </div>
      ) : null}

      {/* Single Report or Gantt Report full height when only one is shown */}
      <div
        className={clsx(
          'w-full flex items-start gap-8 justify-between',
          openState === 0 && 'h-[30vh]', // Normal height when all graphs shown
          (openState === 2 || openState === 3) && 'h-[75vh]' // Full height when single graph
        )}
      >
        {(openState === 0 || openState === 2) && (
          <div className="w-full">
            <SingleReport
              getVideo={getVideo}
              indefenceVideoData={inferenceMockData}
              openState={openState}
              setOpenState={setOpenState}
            />
          </div>
        )}
        {(openState === 0 || openState === 3) && (
          <div className="w-full">
            <GanttReport
              getVideo={getVideo}
              indefenceVideoData={inferenceMockData}
              openState={openState}
              setOpenState={setOpenState}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InferenceReports;
